import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Heart, MessageCircle, UserPlus, Mail, Bell, Check, ArrowLeft, Download, Smartphone, PhoneMissed, Phone, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Navigation from '../components/Navigation';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { Capacitor } from '@capacitor/core';
import VoiceCallModal from '../components/VoiceCallModal';
import { setAppBadge } from '../utils/appBadge';

const Notifications = () => {
  const { t } = useLanguage();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [callBack, setCallBack] = useState(null); // { peerUserId, peerName, peerAvatarUrl }
  const navigate = useNavigate();
  const { isDark } = useTheme();

  useEffect(() => {
    loadNotifications();
  }, []);

  // Whenever the notifications list changes, sync the iOS/Android badge to
  // the real unread count. Fixes the bug where the app icon kept showing N
  // long after the user marked everything as read.
  useEffect(() => {
    const unread = notifications.filter(n => !n.is_read).length;
    setAppBadge(unread);
  }, [notifications]);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/notifications');
      setNotifications(response.data);
    } catch (error) {
      console.error('Error loading notifications:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`/api/notifications/${notificationId}/read`);
      setNotifications(prev => prev.map(n =>
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.post('/api/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setAppBadge(0);
      toast.success('Toutes les notifications ont été marquées comme lues');
    } catch (error) {
      console.error('Error marking all as read:', error);
      toast.error('Erreur');
    }
  };

  const deleteOne = async (notificationId, e) => {
    e?.stopPropagation?.();
    try {
      await axios.delete(`/api/notifications/${notificationId}`);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      toast.error('Suppression échouée');
    }
  };

  const deleteAll = async () => {
    if (!window.confirm('Supprimer toutes les notifications ?')) return;
    try {
      const res = await axios.delete('/api/notifications');
      setNotifications([]);
      setAppBadge(0);
      toast.success(`${res?.data?.deleted_count ?? ''} notifications supprimées`);
    } catch (error) {
      toast.error('Suppression échouée');
    }
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    
    switch (notification.type) {
      case 'like':
      case 'comment':
        if (notification.related_id) {
          navigate(`/feed`);
        }
        break;
      case 'follow':
        if (notification.actor_id) {
          navigate(`/profile/${notification.actor_id}`);
        }
        break;
      case 'message':
        if (notification.related_id) {
          navigate(`/messages/${notification.related_id}`);
        }
        break;
      case 'call_missed':
        // Tap on the notification → navigate to chat with the caller
        if (notification.actor_id) {
          navigate(`/messages/new/${notification.actor_id}`);
        }
        break;
      case 'app_update':
        // Don't navigate - links are shown inline
        break;
      default:
        break;
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'like':
        return <Heart className="w-5 h-5 text-magenta-500" fill="currentColor" />;
      case 'comment':
        return <MessageCircle className="w-5 h-5 text-blue-500" />;
      case 'follow':
        return <UserPlus className="w-5 h-5 text-green-500" />;
      case 'message':
        return <Mail className="w-5 h-5 text-violet-500" />;
      case 'app_update':
        return <Download className="w-5 h-5 text-cyan-400" />;
      case 'call_missed':
        return <PhoneMissed className="w-5 h-5 text-red-400" />;
      default:
        return <Bell className="w-5 h-5 text-gray-400" />;
    }
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return 'À l\'instant';
    if (seconds < 3600) return `Il y a ${Math.floor(seconds / 60)}m`;
    if (seconds < 86400) return `Il y a ${Math.floor(seconds / 3600)}h`;
    if (seconds < 604800) return `Il y a ${Math.floor(seconds / 86400)}j`;
    return date.toLocaleDateString('fr-FR');
  };

  if (loading) {
    return (
      <div className={`min-h-screen text-white flex items-center justify-center pb-20 ${isDark ? 'bg-black' : ''}`} style={!isDark ? { background: 'var(--theme-bg)' } : {}}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-magenta-500 mx-auto mb-4"></div>
          <p className="text-gray-400">{t('common.loading', 'Chargement...')}</p>
        </div>
      </div>
    );
  }

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div className={`min-h-screen text-white pb-20 ${isDark ? 'bg-black' : ''}`} style={!isDark ? { background: 'var(--theme-bg)' } : {}}>
      {/* Header */}
      <div className={`sticky top-0 z-40 backdrop-blur-md border-b border-white/10 p-4 ${isDark ? 'bg-black/90' : 'bg-white/10'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
              title={t('common.back', 'Retour')}
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-2xl font-bold">{t('notificationsPage.title', 'Notifications')}</h1>
              {unreadCount > 0 && (
                <p className="text-sm text-gray-400 mt-1">{unreadCount} {t('notificationsPage.unreadShort', 'non lue(s)')}</p>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="px-3 py-2 bg-white/10 hover:bg-white/20 rounded-full text-xs sm:text-sm font-medium transition-colors flex items-center space-x-1.5"
                data-testid="mark-all-read-btn"
              >
                <Check className="w-4 h-4" />
                <span className="hidden sm:inline">{t('notificationsPage.markAllRead', 'Tout marquer comme lu')}</span>
                <span className="sm:hidden">{t('notificationsPage.markAllReadShort', 'Tout lire')}</span>
              </button>
            )}
            {notifications.length > 0 && (
              <button
                onClick={deleteAll}
                className="px-3 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-full text-xs sm:text-sm font-medium transition-colors flex items-center space-x-1.5"
                title={t('notificationsPage.deleteAll', 'Tout supprimer')}
                data-testid="delete-all-notifications-btn"
              >
                <Trash2 className="w-4 h-4" />
                <span className="hidden sm:inline">{t('notificationsPage.deleteAll', 'Tout supprimer')}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="p-4">
        {notifications.length === 0 ? (
          <div className="text-center py-20">
            <Bell className="w-16 h-16 mx-auto mb-4 text-gray-600" />
            <p className="text-gray-400">{t('notificationsPage.empty', 'Aucune notification')}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={`w-full p-4 rounded-xl transition-colors text-left cursor-pointer ${
                  notification.is_read
                    ? 'bg-white/5 hover:bg-white/10'
                    : notification.type === 'app_update'
                    ? 'bg-cyan-500/10 hover:bg-cyan-500/15 border border-cyan-500/20'
                    : 'bg-magenta-500/10 hover:bg-magenta-500/20 border border-magenta-500/20'
                }`}
              >
                <div className="flex items-start space-x-3">
                  {/* Actor Avatar */}
                  <div className="flex-shrink-0">
                    {notification.type === 'app_update' ? (
                      <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center">
                        <Download className="w-6 h-6 text-white" />
                      </div>
                    ) : notification.actor ? (
                      <div className="w-12 h-12 bg-gradient-to-r from-magenta-500 to-violet-600 rounded-full flex items-center justify-center text-white font-bold">
                        {notification.actor.name?.[0]?.toUpperCase()}
                      </div>
                    ) : (
                      <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center">
                        {getIcon(notification.type)}
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-white">
                        {notification.content}
                      </p>
                      {notification.type === 'call_missed' && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 text-[10px] font-bold uppercase tracking-wide border border-red-500/40"
                          data-testid="missed-call-badge"
                        >
                          <PhoneMissed className="w-3 h-3" /> Manqué
                        </span>
                      )}
                    </div>

                    {/* Call back button for missed calls */}
                    {notification.type === 'call_missed' && notification.actor_id && Capacitor.isNativePlatform() && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          markAsRead(notification.id);
                          setCallBack({
                            peerUserId: notification.actor_id,
                            peerName: notification.actor?.name || notification.actor?.username || 'Utilisateur',
                            peerAvatarUrl: notification.actor?.profile_image_url,
                          });
                        }}
                        className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-violet-600 hover:bg-violet-700 rounded-lg text-sm font-medium text-white transition-colors"
                        data-testid={`callback-btn-${notification.id}`}
                      >
                        <Phone className="w-3.5 h-3.5" /> Rappeler
                      </button>
                    )}
                    
                    {/* App Store / Google Play links for update notifications */}
                    {notification.type === 'app_update' && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        <a
                          href={notification.app_store_url || "https://apps.apple.com/fr/app/destine-studio/id6756779175"}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-black/40 hover:bg-black/60 border border-white/10 rounded-lg text-sm font-medium text-white transition-colors"
                          data-testid="app-store-link"
                        >
                          <Smartphone className="w-3.5 h-3.5" />
                          App Store
                        </a>
                        <a
                          href={notification.play_store_url || "https://play.google.com/store/apps/details?id=app.destine.studio"}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-black/40 hover:bg-black/60 border border-white/10 rounded-lg text-sm font-medium text-white transition-colors"
                          data-testid="play-store-link"
                        >
                          <Smartphone className="w-3.5 h-3.5" />
                          Google Play
                        </a>
                      </div>
                    )}
                    
                    <p className="text-sm text-gray-400 mt-1">
                      {getTimeAgo(notification.created_at)}
                    </p>
                  </div>

                  {/* Unread indicator */}
                  {!notification.is_read && (
                    <div className="w-2 h-2 bg-magenta-500 rounded-full flex-shrink-0 mt-2"></div>
                  )}

                  {/* Delete single notification */}
                  <button
                    onClick={(e) => deleteOne(notification.id, e)}
                    className="opacity-60 hover:opacity-100 p-1.5 hover:bg-white/10 rounded-full text-red-400 flex-shrink-0 self-start"
                    title="Supprimer"
                    data-testid={`delete-notif-${notification.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Navigation />

      {/* Voice call modal for "Rappeler" action */}
      {callBack && (
        <VoiceCallModal
          peerUserId={callBack.peerUserId}
          peerName={callBack.peerName}
          peerAvatarUrl={callBack.peerAvatarUrl}
          onClose={() => setCallBack(null)}
        />
      )}
    </div>
  );
};

export default Notifications;
